import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";

import { ChatMessage as ChatMessageComponent } from "@/components/ai-chat/chat-message";
import { cn } from "@/lib/utils";
import { ChatMessage as ChatMessageType } from "@/types/chatbot.types";
import { fadeInUp } from "@/utils/animations";

type ChatMessagesProps = {
    messages: ChatMessageType[];
    isLoading?: boolean;
    className?: string;
    onReportError?: (message: ChatMessageType) => void;
};

export function ChatMessages({ messages, isLoading = false, className, onReportError }: ChatMessagesProps) {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        console.log("🎨 CHAT MESSAGES RENDERED:", {
            messageCount: messages.length,
            messages: messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: (m.content || "").substring(0, 50) + "...",
            })),
            isLoading,
        });
        scrollToBottom();
    }, [messages, isLoading]);

    return (
        <div className={cn("h-full overflow-y-auto p-4", className)}>
            <div className="space-y-4">
                <AnimatePresence>
                    {messages.map((message, index) => (
                        <motion.div
                            key={`${message.id}-${index}`}
                            {...fadeInUp}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                            <ChatMessageComponent message={message} onReportError={onReportError} />
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* Loading Indicator */}
                {isLoading && (
                    <motion.div {...fadeInUp} className="flex items-start gap-3">
                        <div className="bg-gray-100 dark:bg-white/10 rounded-2xl rounded-tl-md px-4 py-3">
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-100" />
                                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-200" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            <div ref={messagesEndRef} />
        </div>
    );
}
